import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import VideoCard from "../components/VideoCard";

/**
 * Search — universal search results page. Enhanced iter206 with client-
 * side filtering chips so viewers can narrow to Live-only, a specific
 * category, or newest-first without another server round-trip.
 *
 * Server returns `{videos, creators, live}` for the raw text query;
 * filter state (`type`, `category`, `sort`) is layered on top and
 * mirrored into URL params so search results are shareable/back-nav-safe.
 */
const TYPE_CHIPS = [
  { k: "all",     label: "All"    },
  { k: "live",    label: "Live"   },
  { k: "videos",  label: "Videos" },
  { k: "creators",label: "People" },
];

const SORT_CHIPS = [
  { k: "relevance", label: "Relevant" },
  { k: "newest",    label: "Newest"   },
  { k: "popular",   label: "Popular"  },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const type = params.get("type") || "all";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "relevance";
  const [data, setData] = useState({ videos: [], creators: [], live: [] });
  const [cats, setCats] = useState([]);

  useEffect(() => {
    if (!q) { setData({ videos: [], creators: [], live: [] }); return; }
    api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setData(r.data)).catch(() => {});
  }, [q]);

  // Category chips are seeded from the results themselves — no separate
  // fetch. This keeps the chip row relevant to what actually matched.
  useEffect(() => {
    const s = new Set();
    (data.videos || []).forEach((v) => v?.category && s.add(v.category));
    (data.live   || []).forEach((v) => v?.category && s.add(v.category));
    setCats(Array.from(s).slice(0, 12));
  }, [data]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || value === "relevance") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const catMatch = (v) => !category || v?.category === category;

  const filtered = useMemo(() => {
    let videos = (data.videos || []).filter(catMatch);
    let live   = (data.live   || []).filter(catMatch);
    if (sort === "newest") {
      const t = (v) => new Date(v?.created_at || v?.started_at || 0).getTime();
      videos = [...videos].sort((a, b) => t(b) - t(a));
      live   = [...live].sort((a, b) => t(b) - t(a));
    } else if (sort === "popular") {
      const p = (v) => v?.view_count || v?.viewer_count || 0;
      videos = [...videos].sort((a, b) => p(b) - p(a));
      live   = [...live].sort((a, b) => p(b) - p(a));
    }
    return { videos, live, creators: data.creators || [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, category, sort]);

  const showLive     = (type === "all" || type === "live")     && filtered.live.length > 0;
  const showCreators = (type === "all" || type === "creators") && filtered.creators.length > 0;
  const showVideos   = (type === "all" || type === "videos");

  return (
    <div data-testid="search-page" className="pt-32 px-4 sm:px-8 md:px-12 pb-20 max-w-7xl mx-auto">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">Search</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-white tracking-tighter mt-2">&ldquo;{q}&rdquo;</h1>

      {/* Filter chip row — Type / Sort always visible; Category chips only
          when the current results include categorised items. */}
      <div className="mt-5 space-y-2">
        <div className="flex flex-wrap gap-1.5" data-testid="search-filter-type">
          {TYPE_CHIPS.map((c) => (
            <button
              key={c.k}
              type="button"
              onClick={() => setParam("type", c.k)}
              aria-pressed={type === c.k}
              data-testid={`search-filter-type-${c.k}`}
              className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full border ${
                type === c.k
                  ? "bg-tv-accent text-black border-tv-accent"
                  : "bg-white/5 text-white/70 border-white/15 hover:bg-white/10"
              }`}
            >{c.label}</button>
          ))}
        </div>
        {cats.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="search-filter-category">
            <button
              type="button"
              onClick={() => setParam("category", "")}
              aria-pressed={!category}
              data-testid="search-filter-category-any"
              className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                !category
                  ? "bg-white/15 text-white border-white/25"
                  : "bg-white/[0.03] text-white/60 border-white/10 hover:bg-white/[0.06]"
              }`}
            >Any category</button>
            {cats.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setParam("category", c)}
                aria-pressed={category === c}
                data-testid={`search-filter-category-${c}`}
                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                  category === c
                    ? "bg-white/15 text-white border-white/25"
                    : "bg-white/[0.03] text-white/60 border-white/10 hover:bg-white/[0.06]"
                }`}
              >{c}</button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="search-filter-sort">
          {SORT_CHIPS.map((c) => (
            <button
              key={c.k}
              type="button"
              onClick={() => setParam("sort", c.k)}
              aria-pressed={sort === c.k}
              data-testid={`search-filter-sort-${c.k}`}
              className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
                sort === c.k
                  ? "bg-tv-accent/20 text-tv-accent border-tv-accent/40"
                  : "bg-white/[0.03] text-white/60 border-white/10 hover:bg-white/[0.06]"
              }`}
            >{c.label}</button>
          ))}
        </div>
      </div>

      {showLive && (
        <section className="mt-10">
          <h2 className="font-display font-bold text-2xl text-white mb-4">Live</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {filtered.live.map((v) => <VideoCard key={v.id} item={v} variant="wide" live fullWidth />)}
          </div>
        </section>
      )}

      {showCreators && (
        <section className="mt-10">
          <h2 className="font-display font-bold text-2xl text-white mb-4">Creators</h2>
          <div className="flex flex-wrap gap-4">
            {filtered.creators.map((c) => (
              <Link key={c.user_id} to={`/creator/${c.user_id}`} data-testid={`search-creator-${c.user_id}`} className="tv-glass px-4 py-3 rounded-xl flex items-center gap-3 hover:bg-white/10">
                <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="text-white font-semibold">{c.name}</div>
                  <div className="text-xs text-white/50 uppercase">{(c.role || "").replace("_", " ")}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {showVideos && (
        <section className="mt-10">
          <h2 className="font-display font-bold text-2xl text-white mb-4">Videos</h2>
          {filtered.videos.length === 0 ? (
            <div className="text-white/60">No videos match &ldquo;{q}&rdquo;{category && ` in ${category}`}.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filtered.videos.map((v) => <VideoCard key={v.id} item={v} fullWidth />)}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
